// app/javascript/dashboard/routes/dashboard/kanban/index.script.js
import draggable from 'vuedraggable';
import kanbanAPI from 'dashboard/api/kanban';
import conversationAPI from 'dashboard/api/inbox/conversation';

// mixins
import colors from 'dashboard/mixins/kanban/colors';
import dynamicHeights from 'dashboard/mixins/kanban/dynamicHeights';
import dndColumns from 'dashboard/mixins/kanban/dndColumns';
import dndCards from 'dashboard/mixins/kanban/dndCards';
import renameColumnTitle from 'dashboard/mixins/kanban/renameColumnTitle';


// novos componentes
import KanbanColumn from './components/KanbanColumn.vue';
import ImageLightbox from './components/ImageLightbox.vue';

export default {
  name: 'KanbanIndex',
  components: { draggable, KanbanColumn, ImageLightbox },
  mixins: [colors, dynamicHeights, dndColumns, dndCards, renameColumnTitle],

  data() {
    return {
      board: { id: null, name: '', columns: [] },
      loading: true,
      error: null,

      // busca
      searchQuery: '',
      // infinito "Novo"
      novoCursor: null,
      novoLoading: false,
      novoDone: false,
      _virtualListEl: null,
      _virtualScrollHandler: null,

      // lightbox
      imagePreview: { open: false, url: null },

      // ------- AUTOSIZE (NOVO) -------
      _ro: null,
      _onWindowResize: null,
      _onVisibilityChange: null,
      _resizeRAF: null,

      // clique x drag
      _lastDragEndedAt: null,
      // _cardsLists já é usado dinamicamente via (this._cardsLists ||= [])
    };
  },

  computed: {
    accountId() {
      const accountId = Number(this.$route?.params?.accountId);
      return Number.isFinite(accountId) ? accountId : null;
    },
    hasSearch() {
      return (this.searchQuery || '').trim().length > 0;
    },
  },

  async created() {
    try {
      const { data } = await kanbanAPI.getBoard();
      this.board = data;
      this.$nextTick(() => {
        this.deferUpdateHeights?.();
        this._computeInitialNovoCursor();
        this._attachNovoInfiniteScroll();

        // ------- AUTOSIZE (NOVO) -------
        this._setupAutoResize();
      });
    } catch (e) {
      this.error = e?.response?.data?.error || e.message || 'Falha ao carregar';
    } finally {
      this.loading = false;
    }
  },

  beforeUnmount() {
    try {
      if (this._virtualListEl && this._virtualScrollHandler) {
        this._virtualListEl.removeEventListener('scroll', this._virtualScrollHandler);
      }
    } catch {}
    this._virtualListEl = null;
    this._virtualScrollHandler = null;

    // ------- AUTOSIZE (NOVO) - limpeza -------
    try {
      if (this._onWindowResize) {
        window.removeEventListener('resize', this._onWindowResize);
        window.removeEventListener('orientationchange', this._onWindowResize);
      }
      if (this._onVisibilityChange) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange, true);
      }
      if (this._ro && this._ro.disconnect) this._ro.disconnect();
      if (this._resizeRAF) cancelAnimationFrame(this._resizeRAF);
    } catch {}
    this._ro = null;
    this._onWindowResize = null;
    this._onVisibilityChange = null;
    this._resizeRAF = null;
  },

  methods: {
    // -------- utils --------
    _num(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    _unique(arr) {
      const out = [];
      const seen = new Set();
      arr.forEach(n => {
        if (Number.isFinite(n) && !seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
      });
      return out;
    },

    filenameFromUrl(url) {
      try {
        const u = new URL(url);
        const last = u.pathname.split('/').pop() || '';
        return decodeURIComponent(last) || 'arquivo';
      } catch (e) {
        const last = (url || '').split('/').pop() || '';
        try { return decodeURIComponent(last); } catch { return last || 'arquivo'; }
      }
    },

    // ---- busca (mantém lógica existente) ----
    cardVisible(card) {
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return true;
      const hay = [
        card?.contact_name,
        card?.title,
        card?.inbox_name,
        card?.inbox_description,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    },
    columnHasMatch(col) {
      const cards = col?.cards || [];
      return cards.some(c => this.cardVisible(c));
    },

    // lightbox
    openImagePreview(url) {
      if (!url) return;
      this.imagePreview = { open: true, url };
    },
    closeImagePreview() {
      this.imagePreview.open = false;
      this.imagePreview.url = null;
    },

    // utilitário de horário
    formatMsgTime(ts) {
      try {
        const d = ts ? new Date(ts) : null;
        if (!d || isNaN(d.getTime())) return '';
        return d.toLocaleString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        });
      } catch {
        return '';
      }
    },

    // ========= Helpers p/ garantir DISPLAY_ID =========
    async _searchByDisplayId(displayId) {
      try {
        const { data: searchResult } = await conversationAPI.search({
          q: `display_id:${displayId}`,
        });
        const hit = Array.isArray(searchResult?.payload)
          ? searchResult.payload[0]
          : null;
        if (!hit) return null;

        const convDisplayId = this._num(
          hit?.display_id ?? hit?.conversation_display_id
        );
        const inboxId =
          this._num(hit?.meta?.inbox_id) ??
          this._num(hit?.inbox_id) ??
          this._num(hit?.inbox?.id) ??
          null;

        if (Number.isFinite(convDisplayId)) {
          return { displayId: convDisplayId, inboxId };
        }

        return null;
      } catch {
        return null;
      }
    },

    async _showByRealId(realId) {
      try {
        const { data } = await conversationAPI.show(realId);
        const displayId =
          this._num(data?.display_id) ??
          this._num(data?.meta?.display_id) ??
          this._num(data?.additional_attributes?.display_id) ??
          null;
        const inboxId =
          this._num(data?.meta?.inbox_id) ??
          this._num(data?.inbox_id) ??
          this._num(data?.inbox?.id) ??
          null;

        return { displayId, inboxId };
      } catch {
        return { displayId: null, inboxId: null };
      }
    },

    async _resolveDisplayIdAndInbox(card) {
      const displayCandidates = this._unique([
        this._num(card?.conversation_display_id),
        this._num(card?.display_id),
        this._num(card?.conversation?.display_id),
        typeof card?.id === 'string' && card.id.startsWith('conv-')
          ? this._num(card.id.slice(5))
          : null,
      ]).filter(Boolean);

      if (displayCandidates.length) {
        const displayId = displayCandidates[0];
        const inboxId =
          this._num(card?.inbox_id) ??
          this._num(card?.inbox?.id) ??
          this._num(card?.meta?.inbox_id) ??
          null;

        return { displayId, inboxId };
      }

      const conversationCandidates = this._unique([
        this._num(card?.conversation_id),
        this._num(card?.conversation?.id),
        typeof card?.id === 'number' ? this._num(card.id) : null,
      ]).filter(Boolean);

      for (const maybeDisplay of conversationCandidates) {
        const result = await this._searchByDisplayId(maybeDisplay);
        if (result?.displayId) return result;
      }

      for (const realId of conversationCandidates) {
        const result = await this._showByRealId(realId);
        if (Number.isFinite(result.displayId)) return result;
      }

      return { displayId: null, inboxId: null };
    },

    // ============== CLICK: navegar SEMPRE com display_id ==============
    async onCardClickFn(card, evt) {
      try {
        evt?.stopPropagation?.();
        evt?.preventDefault?.();
      } catch {}

      if (!card) return;

      const now = Date.now();
      if (this._lastDragEndedAt && now - this._lastDragEndedAt < 120) return;

      const accountId =
        this._num(this.accountId) ?? this._num(card?.account_id);
      if (!Number.isFinite(accountId)) return;

      const { displayId, inboxId } =
        await this._resolveDisplayIdAndInbox(card);
      if (!Number.isFinite(displayId)) return;

      const target = {
        name: 'inbox_conversation',
        params: {
          accountId: String(accountId),
          conversation_id: String(displayId),
        },
      };

      if (Number.isFinite(inboxId)) {
        target.query = { inbox_id: String(inboxId) };
      }

      try {
        await this.$router.push(target);
      } catch {}
    },

    // chamado pelo <draggable @end="onDragEndFn">
    // chamado pelo <draggable @end="onDragEndFn">
    onDragEndFn() {
      this._lastDragEndedAt = Date.now();

      // libera o clique bem rápido após o drop (evita “fantasma”, mas permite clicar logo em seguida)
      setTimeout(() => {
        this._lastDragEndedAt = 0;
      }, 120);

      try { this.deferUpdateHeights?.(); } catch {}
    },

    // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

    // (todo o resto permanece: isVirtual, registerCardsList, infinito "Novo", etc.)
    isVirtual(col) {
      return typeof col?.id !== 'number' || col?.virtual === true;
    },

    registerCardsList(vmOrEl, col) {
      const el = vmOrEl?.$el || vmOrEl;
      if (el && !this._cardsLists?.includes?.(el)) {
        (this._cardsLists ||= []).push(el);
        this.deferUpdateHeights?.();

        // ------- AUTOSIZE (NOVO) - observar novas listas -------
        this._observeCardsList(el);
      }
      let isVirtualList = false;
      if (col) {
        isVirtualList = this.isVirtual(col);
      } else if (el && el.dataset) {
        const raw = el.dataset.colId;
        const num = Number(raw);
        isVirtualList = !Number.isFinite(num);
      }
      if (isVirtualList) {
        this._virtualListEl = el;
        this._attachNovoInfiniteScroll();
      }
    },

    _computeInitialNovoCursor() {
      const vcol = (this.board?.columns || []).find(c => this.isVirtual(c));
      if (!vcol) { this.novoDone = true; return; }
      const cards = vcol.cards || [];
      if (!cards.length) { this.novoDone = true; return; }
      const ids = cards
        .map(c => Number(c.conversation_id || String(c.id || '').replace('conv-', '')))
        .filter(n => Number.isFinite(n));
      if (!ids.length) { this.novoDone = true; return; }
      this.novoCursor = Math.min(...ids);
      if (cards.length < 50) this.novoDone = true;
    },

    _attachNovoInfiniteScroll() {
      if (!this._virtualListEl) return;
      if (this._virtualScrollHandler) return;
      this._virtualScrollHandler = () => {
        if (this.novoLoading || this.novoDone) return;
        const el = this._virtualListEl;
        if (!el) return;
        const nearBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - 80);
        if (nearBottom) this._loadMoreNovo();
      };
      this._virtualListEl.addEventListener('scroll', this._virtualScrollHandler, { passive: true });
    },

    async _loadMoreNovo() {
      if (this.novoLoading || this.novoDone || !this.novoCursor) return;
      this.novoLoading = true;
      try {
        const { data } = await kanbanAPI.getNewConversations({
          cursor: this.novoCursor,
          limit: 50,
        });
        const vcol = (this.board?.columns || []).find(c => this.isVirtual(c));
        if (!vcol) { this.novoDone = true; return; }
        const newCards = Array.isArray(data?.cards) ? data.cards : [];
        if (newCards.length) {
          vcol.cards.push(...newCards);
          this.novoCursor = data?.next_cursor || null;
          this.novoDone   = !data?.has_more;
        } else {
          this.novoDone = true;
        }
        this.$nextTick(() => this.deferUpdateHeights?.());
      } catch (e) {
        this.error = e?.response?.data?.error || e.message || 'Falha ao carregar mais conversas';
        this.novoDone = true;
      } finally {
        this.novoLoading = false;
      }
    },

    // ================== AUTOSIZE (NOVO) ==================
    _setupAutoResize() {
      this._onWindowResize = () => {
        if (this._resizeRAF) cancelAnimationFrame(this._resizeRAF);
        this._resizeRAF = requestAnimationFrame(() => {
          this.deferUpdateHeights?.();
        });
      };

      this._onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          this._onWindowResize();
        }
      };

      window.addEventListener('resize', this._onWindowResize, { passive: true });
      window.addEventListener('orientationchange', this._onWindowResize, { passive: true });
      document.addEventListener('visibilitychange', this._onVisibilityChange, true);

      try {
        const RO = window.ResizeObserver || ResizeObserver;
        this._ro = new RO(() => this._onWindowResize());
        if (this.$el) this._ro.observe(this.$el);
        // observar listas já registradas, se houver
        (this._cardsLists || []).forEach(el => {
          try { if (el) this._ro.observe(el); } catch {}
        });
      } catch {
        // listeners de window já cobrem
      }
    },

    _observeCardsList(el) {
      try {
        if (this._ro && el) this._ro.observe(el);
      } catch {}
    },
    // =====================================================
  },
};
